export function getNow() {
    return new Date();
}
export function getToday() {
    const now = getNow();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
export function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}
export function addMonths(date, months) {
    return new Date(date.getFullYear(), date.getMonth() + months, date.getDate(), date.getHours(), date.getMinutes(), date.getMilliseconds());
}
export function setMonth(date, month) {
    return new Date(date.getFullYear(), month, date.getDate(), date.getHours(), date.getMinutes(), date.getMilliseconds());
}
export function addYears(date, years) {
    return new Date(date.getFullYear() + years, date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getMilliseconds());
}
export function setYear(date, year) {
    return new Date(year, date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getMilliseconds());
}
export function isToday(date) {
    const today = new Date();
    return (date.getDate() == today.getDate() &&
        date.getMonth() == today.getMonth() &&
        date.getFullYear() == today.getFullYear());
}
export function areDatesEqual(a, b) {
    if (a && b) {
        return (a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate());
    }
    return false;
}
export function isPrintableCharacter(keyCode) {
    return keyCode && keyCode.length === 1;
}
export function isObject(value) {
    return (typeof value === 'object' || isFunction(value)) && !isNull(value) && !isArray(value);
}
export function isLooseObject(value) {
    return !isNull(value) && typeof value === 'object';
}
export function isFunction(value) {
    return typeof value === 'function';
}
export function isNumber(value) {
    return typeof value === 'number' && isFinite(value);
}
export function isString(value) {
    return typeof value === 'string';
}
export function isStringOrNumber(value) {
    return isString(value) || isNumber(value);
}
export function isBoolean(value) {
    return typeof value === 'boolean';
}
export function isEmptyString(value) {
    return isString(value) && value.trim() === '';
}
export function isNull(value) {
    return value === null;
}
export function isUndefined(value) {
    return typeof value === 'undefined';
}
export function isNullish(value) {
    return isNull(value) || isUndefined(value);
}
export function isArray(value) {
    return Array.isArray(value);
}
export function isStringArray(value) {
    return isArray(value) && value.every((v) => typeof v === 'string');
}
export function isNumberArray(value) {
    return isArray(value) && value.every((v) => typeof v === 'number');
}
export function isNumericStringArray(value) {
    return (isArray(value) &&
        value.every((v) => typeof v === 'string') &&
        value.every((v) => !isUndefined(toInteger(v))));
}
export function isEmptyArray(value) {
    return isArray(value) ? value.length === 0 : false;
}
export function isDate(value) {
    return Object.prototype.toString.call(value) === '[object Date]';
}
export function isPromise(value) {
    return isObject(value) && isFunction(value.then);
}
export function isError(error) {
    return error instanceof Error;
}
export function isHasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}
/**
 * @deprecated
 */
export const logger = {
    info(...args) {
        if (process.env.NODE_ENV === 'development') {
            console.log('MP-UI::', ...args);
        }
    },
    error(...args) {
        if (process.env.NODE_ENV === 'development') {
            console.error('MP-UI::', ...args);
        }
    },
    logLastCaller(...args) {
        const caller = new Error().stack?.split('\n')[3].trim().split(' ')[1];
        logger.info(caller, ...args);
    },
};
export function toInteger(value) {
    if (isString(value)) {
        const result = parseInt(value, 10);
        if (!isNaN(result))
            return result;
    }
    if (isNumber(value)) {
        return value;
    }
}
export function objectKeys(obj) {
    return Object.keys(obj);
}
export function deepEqual(x, y) {
    if (x === y) {
        return true;
    }
    else if (isLooseObject(x) && isLooseObject(y)) {
        if (objectKeys(x).length !== objectKeys(y).length) {
            return false;
        }
        for (const key in x) {
            if (isHasOwn(x, key)) {
                if (!deepEqual(x[key], y[key])) {
                    return false;
                }
            }
            else {
                return false;
            }
        }
        return true;
    }
    else {
        return false;
    }
}
export function toggleElementInArray(array, element, predicate) {
    if (predicate) {
        const index = array.findIndex(predicate);
        if (index === -1) {
            return [...array, element];
        }
        else {
            return array.filter((v) => !predicate(v));
        }
    }
    else {
        const index = array.indexOf(element);
        if (index === -1) {
            return [...array, element];
        }
        else {
            return array.filter((v) => v !== element);
        }
    }
}
export function optimisticallyRemoveFromArray(items, predicate) {
    const idx = items.findIndex(predicate);
    const item = items[idx];
    if (item) {
        items.splice(idx, 1);
    }
    return () => {
        item && items.splice(idx, 0, item);
    };
}
export function getLastElement(arr) {
    if (!arr.length) {
        return;
    }
    return arr[arr.length - 1];
}
export function omit(object, ...fields) {
    const result = { ...object };
    fields.forEach(function (field) {
        delete result[field];
    });
    return result;
}
export function pick(obj, ...keys) {
    return Object.fromEntries(keys.filter((key) => key in obj).map((key) => [key, obj[key]]));
}
export function wait(duration) {
    return new Promise((resolve) => {
        const timeout = setTimeout(() => {
            resolve(timeout);
        }, duration);
    });
}
export function areStringsEqual(str1, str2, options = { case: false, trim: true }) {
    if (options.case && options.trim) {
        return str1.trim().toLowerCase() === str2.trim().toLowerCase();
    }
    else if (options.case) {
        return str1.toLowerCase() === str2.toLowerCase();
    }
    else if (options.trim) {
        return str1.trim() === str2.trim();
    }
    return str1 === str2;
}
export function stringIncludes(str, search, options = { case: true, trim: true }) {
    if (options.case && options.trim) {
        return str.toLowerCase().includes(search.toLowerCase());
    }
    else if (options.case) {
        return str.toLowerCase().includes(search);
    }
    else if (options.trim) {
        return str.includes(search.trim());
    }
    return str.includes(search);
}
export function numberRange(from = 0, to = 10) {
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}
export function truncDigits(value, digits) {
    return Math.trunc(value * Math.pow(10, digits)) / Math.pow(10, digits);
}
/**
 * Normalizes a certain number value from one range to another range (default 0-1)
 */
export function normalizeNumericValue(value, min, max, newMin = 0, newMax = 1, precision = 2) {
    const boundedValue = Math.max(min, Math.min(max, value));
    const normalizedValue = ((boundedValue - min) * (newMax - newMin)) / (max - min) + newMin;
    return +normalizedValue.toFixed(precision);
}
export function scrollElementIntoView(element, topOffset = 50) {
    const headerOffset = document.getElementsByTagName('header')?.[0]?.offsetHeight || 0;
    const totalTopOffset = headerOffset + topOffset;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - totalTopOffset;
    window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
    });
}
export function deepValue(obj, path) {
    const parts = path.split('.');
    let result = obj;
    for (const part of parts) {
        if (result && isObject(result)) {
            result = result[part];
        }
        else {
            return undefined;
        }
    }
    return result;
}
export function deduplicateSuggestionsByName(suggestions) {
    const groupedByName = new Map();
    for (const suggestion of suggestions) {
        const normalizedName = suggestion.name.trim();
        if (!groupedByName.has(normalizedName)) {
            groupedByName.set(normalizedName, []);
        }
        groupedByName.get(normalizedName).push(suggestion);
    }
    return Array.from(groupedByName.entries()).map(([name, groupedSuggestions]) => {
        const primarySuggestion = groupedSuggestions[0];
        return {
            id: primarySuggestion.id,
            name: name,
            originalIds: groupedSuggestions.map(s => s.id),
            suggestions: groupedSuggestions,
        };
    });
}
export function expandDeduplicatedSuggestions(deduplicatedSuggestions) {
    return deduplicatedSuggestions.flatMap(deduplicated => deduplicated.suggestions);
}
//# sourceMappingURL=index.js.map